import type { FC } from 'react';

import React from 'react';
import { Link } from 'react-router';
import styled, { css } from 'styled-components';

import { clampPage, pageCount } from 'src/utils/catalogUrl';

interface PaginationProps {
  /** How many cars the catalog view holds — the `total` of the api answer. */
  total: number;
  /** The page the address asks for. */
  page: number;
  /** The address of a page: the same view, another page (`useCatalogRoute().pathTo({ page })`). */
  hrefFor: (page: number) => string;
}

type PageSlot = number | 'gap';

/**
 * The page numbers shown around the current one: the first, the one before, the current, the one
 * after and the last, with a gap where pages are left out — or the page itself where the gap would
 * hide just one, since a lone number takes no more room than the dots and says more.
 */
export const pageWindow = (current: number, last: number): PageSlot[] => {
  const shown = [...new Set([1, current - 1, current, current + 1, last])]
    .filter((page) => page >= 1 && page <= last)
    .sort((a, b) => a - b);
  const slots: PageSlot[] = [];

  shown.forEach((page, index) => {
    const previous = shown[index - 1];

    if (previous !== undefined && page - previous === 2) slots.push(previous + 1);
    else if (previous !== undefined && page - previous > 2) slots.push('gap');
    slots.push(page);
  });

  return slots;
};

/**
 * Pages of a catalog as real links (storefront-catalog spec §4.1): every page has an address, so a
 * page can be shared, reloaded and reached with Back — none of which a "Load more" list could do.
 * It is a `nav` landmark named "Pagination" (WAI-ARIA), the current page says `aria-current="page"`,
 * and "Previous" on the first page and "Next" on the last are words, not links to where you are.
 *
 * One page is no pagination: nothing renders.
 */
export const Pagination: FC<PaginationProps> = ({ total, page, hrefFor }) => {
  const last = pageCount(total);

  if (last <= 1) return null;

  const current = clampPage(page, total);

  return (
    <Pager aria-label="Pagination">
      <Pages>
        <li>
          {current > 1 ? (
            <PageLink to={hrefFor(current - 1)} rel="prev">
              Previous
            </PageLink>
          ) : (
            <PageEdge>Previous</PageEdge>
          )}
        </li>
        {pageWindow(current, last).map((slot, index) =>
          slot === 'gap' ? (
            <li key={`gap-${index}`} aria-hidden="true">
              <Gap>…</Gap>
            </li>
          ) : (
            <li key={slot}>
              <PageLink
                to={hrefFor(slot)}
                aria-current={slot === current ? 'page' : undefined}
                $number
                $current={slot === current}
              >
                {slot}
              </PageLink>
            </li>
          )
        )}
        <li>
          {current < last ? (
            <PageLink to={hrefFor(current + 1)} rel="next">
              Next
            </PageLink>
          ) : (
            <PageEdge>Next</PageEdge>
          )}
        </li>
      </Pages>
    </Pager>
  );
};

const Pager = styled.nav`
  width: 100%;
  display: flex;
  justify-content: center;
`;

/* It wraps rather than overflows: a window of pages is wider than a phone once the numbers grow. */
const Pages = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

/* Every target is a thumb wide and tall (SF-I3). Words are text; page numbers take the display face,
   like every number on the page. */
const target = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: ${({ theme }) => theme.control.touchTarget}px;
  min-height: ${({ theme }) => theme.control.touchTarget}px;
  padding: 0 ${({ theme }) => theme.space.sm}px;
  border-radius: ${({ theme }) => theme.borderRadius.round}px;
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.large.fontSize}px;
  line-height: ${({ theme }) => theme.typography.large.lineHeight}px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

/* The current page is the accent with black text on it — the pager's one lime block; the others
   are raised surfaces with secondary text. */
const PageLink = styled(Link)<{ $number?: boolean; $current?: boolean }>`
  ${target};
  text-decoration: none;
  border: 1px solid ${({ theme, $current }) => ($current ? theme.colors.accent : theme.colors.border)};
  background: ${({ theme, $current }) => ($current ? theme.colors.accent : theme.colors.surfaceRaised)};
  color: ${({ theme, $current }) => ($current ? theme.colors.accentInk : theme.colors.lightWhite)};

  ${({ theme, $number }) =>
    $number &&
    css`
      font-family: ${theme.fonts.display};
      font-weight: ${theme.fontWeights.bold};
    `}

  &:hover {
    background: ${({ theme, $current }) => ($current ? theme.colors.accentHover : theme.colors.surfaceHover)};
  }

  &:focus-visible {
    outline: var(--gac-control-focus-ring) solid var(--gac-text-primary);
    outline-offset: 2px;
  }
`;

/** "Previous" on the first page, "Next" on the last: there is nowhere to go, so it is not a link.
    Muted, not the disabled grey — the word is still read, and it keeps the contrast text needs (SF-J4). */
const PageEdge = styled.span`
  ${target};
  color: ${({ theme }) => theme.colors.mutedWhite};
`;

const Gap = styled.span`
  ${target};
  min-width: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.mutedWhite};
`;
